import React, { createContext, useContext } from "react";
import { useLocalStorage } from "@mantine/hooks";

export const THEME_COLORS = ["cyan", "blue", "teal", "violet", "orange"];

function hexToRgb(hex) {
  const h = hex.replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const bigint = parseInt(full, 16);
  return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
}

function rgbToHex(r, g, b) {
  const toHex = (n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function mix(hex, targetHex, weight) {
  const [r, g, b] = hexToRgb(hex);
  const [tr, tg, tb] = hexToRgb(targetHex);
  return rgbToHex(r + (tr - r) * weight, g + (tg - g) * weight, b + (tb - b) * weight);
}

// Approximates a Mantine-style 10-shade palette (0 lightest -> 9 darkest,
// index 6 ~= the base tone) from a single custom hex color.
export function generateShades(baseHex) {
  return [
    mix(baseHex, "#ffffff", 0.9),
    mix(baseHex, "#ffffff", 0.75),
    mix(baseHex, "#ffffff", 0.6),
    mix(baseHex, "#ffffff", 0.45),
    mix(baseHex, "#ffffff", 0.3),
    mix(baseHex, "#ffffff", 0.15),
    baseHex,
    mix(baseHex, "#000000", 0.15),
    mix(baseHex, "#000000", 0.3),
    mix(baseHex, "#000000", 0.45),
  ];
}

const ThemeColorContext = createContext(null);

export function ThemeColorProvider({ children }) {
  const [primaryColor, setPrimaryColor] = useLocalStorage({
    key: "mb-primary-color",
    defaultValue: "cyan",
    getInitialValueInEffect: false,
  });
  const [customColor, setCustomColorRaw] = useLocalStorage({
    key: "mb-custom-color",
    defaultValue: "#228be6",
    getInitialValueInEffect: false,
  });

  const setCustomColor = (hex) => {
    setCustomColorRaw(hex);
    setPrimaryColor("custom");
  };

  return (
    <ThemeColorContext.Provider
      value={{ primaryColor, setPrimaryColor, customColor, setCustomColor }}
    >
      {children}
    </ThemeColorContext.Provider>
  );
}

export function useThemeColor() {
  return useContext(ThemeColorContext);
}
